'use client'

import { useCart } from '@shopify/hydrogen-react'
import { useQuery } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { fetchCartPurchasingCompany } from '@/app/actions/cart.actions'
import { fetchCustomerRequest } from '@/app/actions/customer.actions'
import { useCustomerMutations } from '@/hooks/useCustomerMutations'

import { CUSTOMER_QUERY_KEY } from './customer.constants'
import { clearCartStorage, getCustomerState, getLocations } from './customer.utils'

import type { CustomerContextValue, PurchasingCompanyLocation } from './customer.types'
import type { OnboardingState } from '@/components/common/Onboarding/Onboarding.types'
import type { DropdownOptionsType } from '@/components/primitives/Dropdown/Dropdown'
import type { CompanyLocation } from '@/types/shopify/admin/admin.types'
import type { Customer, Customer as CustomerType } from '@/types/shopify/customer-account'
import type { ReactNode } from 'react'

const CustomerContext = createContext<CustomerContextValue | undefined>(undefined)

// Cap auto-initialisation attempts per target location so a cart whose purchasingCompany never
// resolves (e.g. company/location still provisioning) can't trigger an endless create/re-fetch loop.
const MAX_CART_INIT_ATTEMPTS = 3

export function CustomerProvider({ children, customerAccessToken, initialData }: { children: ReactNode; customerAccessToken: string; initialData: CustomerType | null }) {
  const {
    data: customer,
    isLoading: isCustomerLoading,
    error: customerError,
  } = useQuery({
    queryKey: [...CUSTOMER_QUERY_KEY, customerAccessToken],
    initialData: initialData,
    queryFn: async () => {
      if (!customerAccessToken) throw new Error('No access token provided')

      const { customer, error } = await fetchCustomerRequest()

      if (error) {
        throw error
      }

      return customer
    },
    enabled: !!customerAccessToken,
    select(customer: Customer | null) {
      if (!customer) return null

      const customerState = getCustomerState(customer)

      const { locationOptions, locationMap } = getLocations(customer)

      const companyId = customer?.companyContacts?.nodes?.[0]?.company?.id
      const defaultLocation = customer?.companyContacts?.nodes?.[0]?.company?.locations?.nodes?.[0]

      return {
        ...customer,
        customerState,
        locationOptions,
        locationMap,
        defaultLocation,
        companyId,
      }
    },
    // This is required as the Company/Location is created via Shopify Flow and is sometimes not available immediately
    // The reason company/location is created is that every customer needs to have a company/location associated with them
    // Contract Numbers must be associated to a Location, actioning the company create on the frontend could mean some customers are missing company/locations (if never onboarded)
    refetchInterval(query) {
      if (query.state.data?.companyContacts?.nodes?.[0]?.company?.locations?.nodes?.length) {
        return false
      }

      return 1000
    },
  })

  // Define the customer mutations
  const customerMutations = useCustomerMutations(customer?.id, customerAccessToken)

  // Cart/Location Handlers

  const accountState = customer?.customerState
  const locationOptions = customer?.locationOptions
  const { cartCreate, cartReady, buyerIdentity, status, id: cartId } = useCart()

  // Shopify resolves a cart's company-location context asynchronously: the cart returned by
  // cartCreate does NOT include purchasingCompany, so hydrogen-react's local cart state is stale
  // right after a create. We re-fetch the cart (see effect below) and key the resolved location to
  // the cart id so it can't leak across carts/customers.
  const [resolvedLocation, setResolvedLocation] = useState<{ cartId: string; location: PurchasingCompanyLocation | null }>({ cartId: '', location: null })

  // Bumped when cart purchasingCompany resolution fails or returns no location, so the init effect
  // can re-run (bounded by MAX_CART_INIT_ATTEMPTS) instead of stalling with awaitingResolveRef stuck.
  const [resolutionRetry, setResolutionRetry] = useState(0)

  // Tracks that a create is in flight and its purchasingCompany hasn't resolved yet, so the init
  // effect below doesn't re-create the cart in a loop while the re-fetch is pending.
  const awaitingResolveRef = useRef(false)

  // Counts auto-init attempts for the current target location (see MAX_CART_INIT_ATTEMPTS).
  const initAttemptsRef = useRef<{ key: string; count: number }>({ key: '', count: 0 })

  // Hydrogen drops purchasingCompany after mutations that omit it; fall back to the re-fetched value,
  // keyed to cartId so it can't leak across carts (a location change makes a new cart, new id).
  // hydrogen-react types cart fields as deeply optional, so guard on id and coalesce the rest.
  const rawLocation = buyerIdentity?.purchasingCompany?.location
  const hydrogenLocation: PurchasingCompanyLocation | null = rawLocation?.id
    ? {
        id: rawLocation.id,
        name: rawLocation.name ?? '',
        externalId: rawLocation.externalId ?? null,
      }
    : null
  const resolvedLocationForCart = cartId && resolvedLocation.cartId === cartId ? resolvedLocation.location : null
  const purchasingCompanyLocation = hydrogenLocation ?? resolvedLocationForCart
  // Separate primitive so the re-fetch effect keys off hydrogen's own value.
  const cartLocationId = hydrogenLocation?.id ?? ''
  const locationId = purchasingCompanyLocation?.id ?? ''

  // Company Locations
  const handleBuyerIdentityChange = useCallback(
    (locationId: string) => {
      // We must use cartCreate as overriding the cart buyerIdentity does not work as expected
      if (locationId) {
        // Mark that we're waiting on Shopify to resolve purchasingCompany for the new cart so the
        // init effect doesn't re-create it in a loop while the re-fetch below is in flight.
        awaitingResolveRef.current = true
        cartCreate({
          buyerIdentity: {
            companyLocationId: locationId,
          },
          attributes: [
            {
              key: '_location_id',
              value: locationId,
            },
          ],
        })
      } else {
        // eslint-disable-next-line no-console
        console.error('No location ID found for the selected address')
      }
    },
    [cartCreate],
  )

  // Re-fetch the cart after it settles to obtain the resolved purchasingCompany.
  // cartCreate returns the cart before Shopify has attached the company-location context, so
  // buyerIdentity.purchasingCompany is null in hydrogen-react's local state; a fresh read has it.
  useEffect(() => {
    if (!cartReady || !cartId) return
    // Wait for in-flight cart ops to settle so we read the final state
    if (status === 'updating' || status === 'creating' || status === 'fetching') return
    // hydrogen already has the resolved location (e.g. after a fetch on load) — nothing to do
    if (cartLocationId) return
    // Already resolved for this exact cart
    if (resolvedLocation.cartId === cartId) return

    let cancelled = false

    fetchCartPurchasingCompany(cartId)
      .then(({ location }) => {
        if (cancelled) return
        if (location?.id) {
          setResolvedLocation({ cartId, location })
        } else {
          setResolutionRetry((value) => value + 1)
        }
      })
      .catch(() => {
        if (!cancelled) setResolutionRetry((value) => value + 1)
      })
      .finally(() => {
        if (!cancelled) awaitingResolveRef.current = false
      })

    return () => {
      cancelled = true
    }
  }, [cartReady, cartId, status, cartLocationId, resolvedLocation.cartId])

  // Initialise the cart with the first location.
  // Fires cartCreate once per customer login; waits for in-flight cart ops to settle before re-evaluating
  useEffect(() => {
    if (!cartReady) return

    // We don't want to fire this if the cart is in the process of being updated, created, or fetched to prevent race conditions
    // Using the handleBuyerIdentityChange function makes the status update, we want to check the conditions as to not re-trigger the useEffect.
    if (status === 'updating' || status === 'creating' || status === 'fetching') return

    // Uses a primitive here to check, as we are not able to compare locationOptions
    // Not onboarded or no locations: clear the persisted cart so it does not carry
    // a previous user's location-based buyerIdentity into the next session.
    if (accountState !== 'ONBOARDED' || !locationOptions?.length) {
      clearCartStorage()

      return
    }

    // A cart already exists but its purchasingCompany hasn't resolved yet (awaiting the re-fetch
    // effect above). Don't re-create it — that would loop until the location resolves. Just wait.
    if (cartId && !locationId && awaitingResolveRef.current) {
      return
    }

    // We need to check this, as the cart object is not associated to a customer. This is a limitation of the Shopify Hydrogen Cart API
    // This is an issue as we have location based pricing, and setting buyerIdentity against a cart is a workaround to get the correct pricing (Business Logic is that users can't change addresses in Checkout, this is the only way to do so to use Company Locations)
    // With the restrictions, it's possible that a user can sign in as Customer A with locationID === 'gid//location/1', then login as Customer B with locationID === 'gid//location/2'.
    // The cart in localStorage still has the buyerIdentity set to 'gid//location/1'.
    // A falsey value meanas that the scenario above is happening, and we would want to basically re-initialise the cart with the new customer's location.
    const hasMatchingLocation = !!locationId && locationOptions.some((option) => option.value === locationId)

    if (hasMatchingLocation) {
      // Cart is correctly initialised — clear the attempt counter for future re-inits.
      initAttemptsRef.current = { key: '', count: 0 }

      return
    }

    // Stop after MAX_CART_INIT_ATTEMPTS creates for the same target: each failed cycle spawns a new
    // cart, so a location whose purchasingCompany never resolves would otherwise loop indefinitely.
    const targetLocation = locationOptions[0].value
    const attempts = initAttemptsRef.current.key === targetLocation ? initAttemptsRef.current.count : 0

    if (attempts >= MAX_CART_INIT_ATTEMPTS) {
      // eslint-disable-next-line no-console
      console.error(`Cart purchasingCompany did not resolve after ${MAX_CART_INIT_ATTEMPTS} attempts for location ${targetLocation}`)

      return
    }

    initAttemptsRef.current = { key: targetLocation, count: attempts + 1 }

    handleBuyerIdentityChange(targetLocation)
  }, [locationId, status, accountState, locationOptions, handleBuyerIdentityChange, cartReady, cartId, resolutionRetry])

  return (
    <CustomerContext.Provider
      value={{
        customerAccessToken,
        customer: customer as Customer & {
          customerState: OnboardingState
          locationOptions: DropdownOptionsType
          locationMap: Map<string, { postcode: string; displayAddress: string; name: string }>
          defaultLocation: CompanyLocation
          companyId: string
        },
        handleBuyerIdentityChange,
        purchasingCompanyLocation,
        isLoading: isCustomerLoading,
        error: customerError,
        mutations: customerMutations,
      }}
    >
      {children}
    </CustomerContext.Provider>
  )
}

export function useCustomer() {
  const ctx = useContext(CustomerContext)
  if (!ctx) {
    throw new Error('useCustomer must be used within a CustomerProvider')
  }

  return ctx
}
